import { shiftDateInput, todayAsDateInput } from '@/lib/format'
import { messages } from '@/lib/messages'

/**
 * Dönem filtresi (docs/REPORT-DOCUMENTS-PLAN.md §7.2): ilk sürümde yalnız ay, `YYYY-MM`.
 * Hepsi saf takvim aritmetiğidir; `new Date` ile yerel saat yorumlanmaz (AGENTS.md §5).
 * "Bugün" `todayAsDateInput` ile İstanbul takvim günüdür.
 */

const PERIOD_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/
/** Dönem filtresinin göreli varsayılanı: `-1m` geçen ay, `-1y` 12 ay önce. */
const RELATIVE_PERIOD_PATTERN = /^([+-])(\d+)([my])$/

export function isValidPeriod(value: unknown): value is string {
  return typeof value === 'string' && PERIOD_PATTERN.test(value)
}

/** Ay sırası (yıl * 12 + ay); ay aritmetiği için. */
function monthIndex(period: string): number {
  const [, year, month] = PERIOD_PATTERN.exec(period) ?? []
  if (!year) throw new Error(`Geçersiz dönem: ${period}`)
  return Number(year) * 12 + Number(month) - 1
}

function fromMonthIndex(index: number): string {
  const year = Math.floor(index / 12)
  return `${String(year).padStart(4, '0')}-${String((index % 12) + 1).padStart(2, '0')}`
}

export function shiftPeriod(period: string, months: number): string {
  return fromMonthIndex(monthIndex(period) + months)
}

export function previousPeriod(period: string): string {
  return shiftPeriod(period, -1)
}

/** Dönemin ilk ve son takvim günü, her iki uç dahil (Cube `dateRange` biçimi). */
export function periodRange(period: string): [string, string] {
  const next = shiftPeriod(period, 1)
  return [`${period}-01`, shiftDateInput(`${next}-01`, -1)]
}

/** `-1m` -> bugünün (İstanbul) ayından bir önceki dönem. */
export function resolveRelativePeriod(token: string, today = todayAsDateInput()): string {
  const match = RELATIVE_PERIOD_PATTERN.exec(token)
  if (!match) throw new Error(`Geçersiz göreli dönem: ${token}`)
  const [, sign, amount, unit] = match
  const months = Number(amount) * (unit === 'y' ? 12 : 1) * (sign === '-' ? -1 : 1)
  return shiftPeriod(today.slice(0, 7), months)
}

/** `2026-09` -> "Eylül 2026". */
export function periodLabel(period: string): string {
  const [, year, month] = PERIOD_PATTERN.exec(period) ?? []
  if (!year) throw new Error(`Geçersiz dönem: ${period}`)
  return `${messages.period.months[Number(month) - 1]} ${year}`
}

/** Belge şablonunun `period` filtresi (`report-document.ts`); sayfa raporlarında bulunmaz. */
export interface PeriodFilterDef {
  member: string
  type: 'period'
  /** Göreli (`-1m`); yalnız adres çubuğu boşken çözülür. */
  default?: string
}
